<template>
  <div
    class="font-regular"
    :style="{ backgroundImage: `url(${backgroundImageGreen})` }"
  >
    <AgeCheck v-if="globalStore.currentStep === 'ageCheck'" />
    <ShotAnimation v-else-if="globalStore.currentStep === 'shotAnimation'" />
    <TermsAcceptance
      v-else-if="globalStore.currentStep === 'termsAcceptance'"
    />
    <PeopleCheck v-else-if="globalStore.currentStep === 'peopleCheck'" />
    <AgeCheckFailed v-else-if="globalStore.currentStep === 'ageCheckFailed'" />
    <GameMove v-else-if="globalStore.currentStep === 'gameMove'" />
    <PhotoCapture v-else-if="globalStore.currentStep === 'photoCapture'" />
    <ShareInstructions v-else-if="globalStore.currentStep === 'shareInstructions'" />
    <RaffleMove v-else-if="globalStore.currentStep === 'raffleMove'" />
    <TermsAndConditions
      v-else-if="globalStore.currentStep === 'termsAndConditions'"
    />
    <ThatsEnough v-else-if="globalStore.currentStep === 'thatsEnough'" />
  </div>
</template>

<script lang="ts" setup>
import { useGlobalStore } from "./stores/global";
import AgeCheck from "./pages/ageCheck.vue";
import TermsAcceptance from "./pages/termsAcceptance.vue";
import PeopleCheck from "./pages/peopleCheck.vue";
import { onMounted, onBeforeUnmount, watch, ref } from "vue";
import AgeCheckFailed from "./pages/ageCheckFailed.vue";
import GameMove from "./pages/gameMove.vue";
import PhotoCapture from "./pages/photoCapture.vue";
import ShareInstructions from "./pages/shareInstructions.vue";
import RaffleMove from "./pages/raffleMove.vue";
import TermsAndConditions from "./pages/termsAndConditions.vue";
import i18n from "@/plugins/i18n";
import ShotAnimation from "./components/ShotAnimation.vue";
import backgroundImageGreen from "@/assets/images/backgrounds/bg-green-xl.jpg";
import ThatsEnough from "./pages/thatsEnough.vue";
import { useGameTracking } from "@/composables/useGameTracking";

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();
const sessionInitialized = ref(false);

const preventTouchMove = (e: TouchEvent) => {
  if (e.cancelable) {
    e.preventDefault();
  }
};
const preventTouchMoveOptions = { passive: false };

const removeScroll = () => {
  document.addEventListener(
    "touchmove",
    preventTouchMove,
    preventTouchMoveOptions
  );
  console.log("scroll removed");
};

const addScroll = () => {
  document.removeEventListener(
    "touchmove",
    preventTouchMove,
    preventTouchMoveOptions
  );
  console.log("scroll added");
};

watch(
  () => globalStore.currentStep,
  async (newStep, oldStep) => {
    if (newStep === "termsAndConditions") {
      addScroll();
    } else {
      removeScroll();
    }
    
    // Only update session if it's already initialized and step actually changed
    // Skip gameMove and thatsEnough as they are handled by game_started and game_finished calls
    // This tracks: ageCheck, shotAnimation, peopleCheck, ageCheckFailed, termsAndConditions
    if (
      sessionInitialized.value &&
      newStep &&
      newStep !== oldStep &&
      newStep !== "gameMove" &&
      newStep !== "photoCapture" &&
      newStep !== "shareInstructions" &&
      newStep !== "thatsEnough"
    ) {
      console.log(`[App] Step changed to '${newStep}', updating session`);
      try {
        await session.updateSessionProgress(kpiTracker.getKpiData(), { step: newStep });
        console.log(`[App] Session updated with step '${newStep}'`);
      } catch (error) {
        console.error(`[App] Error updating session with step '${newStep}':`, error);
      }
    }
  }
);

// Handle page unload - update session before user leaves
// Use sendBeacon/fetch with keepalive to ensure the request is sent even when closing
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  // Use the special unload function that uses sendBeacon or fetch with keepalive
  // This ensures the request is sent even when the page is closing
  session.updateSessionProgressOnUnload(kpiTracker.getKpiData());
};

onMounted(async () => {
  let lang = new URL(location.href).searchParams.get("lang");
  if (lang) {
    i18n.global.locale = lang;
  }
  
  // Change step FIRST to show correct UI immediately (before session initialization)
  // The watch won't trigger updateSessionProgress because sessionInitialized is still false
  globalStore.changeStep("ageCheck");
  
  // Handle scroll removal for initial step
  if (globalStore.currentStep === "termsAndConditions") {
    addScroll();
  } else {
    removeScroll();
  }
  
  // Initialize session after changing step
  // IMPORTANT: Don't reset progress to 0 - it will be loaded from existing session if available
  const campaignId = new URL(location.href).searchParams.get("campaignId") ?? "";
  console.log("[App] Initializing session on page load");
  try {
    await session.getFingerprintAndInitializeSession(campaignId);
    // DON'T update session progress here - it's already handled in createUpdateSession
    // Updating here would overwrite the progress that was just loaded from the existing session
    const currentProgress = kpiTracker.progress?.value ?? 0;
    console.log("[App] Session initialized, current progress:", currentProgress);
    sessionInitialized.value = true;
  } catch (error) {
    console.error("[App] Error initializing session on page load:", error);
    sessionInitialized.value = true; // Set to true even on error to allow future updates
  }
  
  // Add beforeunload listener
  window.addEventListener("beforeunload", handleBeforeUnload);
});

onBeforeUnmount(() => {
  // Remove beforeunload listener
  window.removeEventListener("beforeunload", handleBeforeUnload);
});
</script>

<style lang="scss" scoped>
@import "@/assets/sass/main.sass";
</style>
